import _regeneratorRuntime from '@babel/runtime/regenerator';
import _asyncToGenerator from '@babel/runtime/helpers/asyncToGenerator';
import cookies from '../node_modules/@buzzfeed/bf-utils/lib/cookies/index.js';
import { getDataFromLayers } from './utils.js';
import commonClientMetadata from './layers/common-client-metadata/index.js';
import commonClientMetadataEvent from './layers/common-client-metadata/event.js';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import commonClientMetadataAmpAdjacent from './layers/common-client-metadata/amp-adjacent.js';
import commonWebClientMetadata from './layers/common-web-client-metadata/index.js';
import universalMetadata from './layers/universal-metadata/index.js';
import './layers/index.js';
import { createClientBroadcaster } from './client-broadcaster.js';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';

var _excluded = ["page_edition"];
/**
 * Extracts the smoke test ID from the url if present
 * and adds the is_bot property
 * @return {Object} - The smoke test metadata
 */

function smokeTestMetadata() {
  var isBot = cookies.get('is_bot') === 'true';
  var isSmokeTest = window.location.search.includes('e2e_test');

  if (isSmokeTest) {
    try {
      // Use URLSearchParams which is supported on the browsers we test (fails gracefully otherwise)
      var params = new URLSearchParams(window.location.search);
      return {
        e2e_test: params.get('e2e_test'),
        is_bot: true
      };
    } catch (_unused) {}
  }

  if (isBot) {
    return {
      is_bot: true
    };
  }

  return {};
}
/**
 * This method creates an event broadcaster.
 * @param  {Object} options.amp_options - Optional amp configuration.
 * @param  {string} options.env- 'dev', 'stage', and 'prod'
 * @param  {string} options.source - Examples: `web_bf`, `web_news`, `web_tasty` and other established source values used for event routing
 * @return {function} - An event creator Function
 */


var createClientEvent = function createClientEvent(_ref) {
  var _ref$amp_options = _ref.amp_options,
      amp_options = _ref$amp_options === void 0 ? {} : _ref$amp_options,
      env = _ref.env,
      source = _ref.source;

  /**
   * Can't create a client event when not run on the client side! When executing on the server,
   * return a no-op function.
   */
  if (typeof window === 'undefined') {
    return function () {};
  }

  var sendClientEvent = createClientBroadcaster({
    env: env
  });
  /**
   * A wrapper for a client event broadcaster function. First argument is an option object
   * configuring the type of event. Additional arguments are for providing layer data.
   * @param {Object} options - Event broadcaster options
   * @param {boolean} [options.flush=false] - Send the event immediately(true) or add to event queue (false)
   * @param {Array} [options.required_layers=[]] - Optional list of required layers for the event being configured.
   * @param {number} [options.sample_rate=1] - Rate at which to sample event requests (between 0 and 1)
   * @param {string} options.type - Event type used for event routing. Examples: 'web_pageview', 'web_impression'
   * @param  {...(Object|function)} layers
   */

  return /*#__PURE__*/function () {
    var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(_ref2) {
      var _ref2$flush,
          flush,
          _ref2$required_layers,
          required_layers,
          _ref2$sample_rate,
          sample_rate,
          type,
          _len,
          layers,
          _key,
          _yield$getDataFromLay,
          page_edition,
          layer_data,
          event_layers,
          event_data,
          _args = arguments;

      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              _ref2$flush = _ref2.flush, flush = _ref2$flush === void 0 ? false : _ref2$flush, _ref2$required_layers = _ref2.required_layers, required_layers = _ref2$required_layers === void 0 ? [] : _ref2$required_layers, _ref2$sample_rate = _ref2.sample_rate, sample_rate = _ref2$sample_rate === void 0 ? 1 : _ref2$sample_rate, type = _ref2.type;

              for (_len = _args.length, layers = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
                layers[_key - 1] = _args[_key];
              }

              _context.next = 4;
              return getDataFromLayers({
                context: {
                  env: env
                },
                layers: layers
              });

            case 4:
              _yield$getDataFromLay = _context.sent;
              page_edition = _yield$getDataFromLay.page_edition;
              layer_data = _objectWithoutProperties(_yield$getDataFromLay, _excluded);
              event_layers = [
              /**
               * Universal and common layers included in ALL events:
               *
               * NOTE:
               * Layer order is intentional and purposefully not alphabetized. This ordering helps display
               * a useful request preview in devtools which helps debugging and QA'ing the requests.
               */
              universalMetadata({
                source: source,
                type: type
              }), commonClientMetadataEvent(), commonClientMetadata({
                // Used to create a unique key for the returned memoized function
                url: document.URL
              }), commonClientMetadataAmpAdjacent(amp_options), commonWebClientMetadata({
                page_edition: page_edition
              })].concat(layers, _toConsumableArray(required_layers.map(function (layer) {
                return layer(layer_data);
              })), [
              /**
               * Include smoketest metadata in the event data if this is an end to end test
               * (`&e2e_test=[timestamp]`).
               */
              smokeTestMetadata()]);
              /**
               * Finally, get a data object from all combined layers. This object is the data sent in the
               * event request body.
               */

              _context.next = 10;
              return getDataFromLayers({
                context: {
                  env: env
                },
                layers: event_layers
              });

            case 10:
              event_data = _context.sent;

              if (!(window.location.search.includes('e2e_test') || Math.random() <= sample_rate)) {
                _context.next = 14;
                break;
              }

              _context.next = 14;
              return sendClientEvent(event_data, {
                flush: flush
              });

            case 14:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }));

    return function (_x) {
      return _ref3.apply(this, arguments);
    };
  }();
};

export { createClientEvent };
